import React, { useCallback, useContext, useEffect, useState } from "react";
import AdminPageTitle from "./admin-page-title.tsx";
import { NotificationData } from "../../entry/server/user.ts";
import { userRepo } from "../../repo/user-repo.ts";
import { AppContext } from "../../context/app-context.tsx";
import { Pagination } from "../../lib/api.ts";
import PaginationView from "../../componrnts/pagination.tsx";
import NotificationSendForm from "../../componrnts/notification-send-form.tsx";
import { toTehranDateString } from "../../utils/common_helpers.ts";

function recipientLabel(notification: NotificationData) {
  if (notification.user_id === 0) return "همه کاربران";
  const name = notification.name || notification.username;
  return name ? `${name} (#${notification.user_id})` : `کاربر #${notification.user_id}`;
}

export default function NotificationsScreen() {
  const { setMessage } = useContext(AppContext);
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const response = await userRepo.getNotifications(page);
      if (!response.status || !response.data) {
        setMessage({ type: "error", message: response.message || "دریافت لیست اعلان‌ها ناموفق بود." });
        setNotifications([]);
        return;
      }
      setNotifications(response.data);
      setPagination(response.pagination ?? null);
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [page, setMessage]);

  useEffect(() => {
    void loadNotifications();
  }, [loadNotifications]);

  const onDelete = async (id: number) => {
    if (!window.confirm("این اعلان حذف شود؟")) return;

    try {
      const response = await userRepo.deleteNotification(id);
      if (!response.status) {
        setMessage({ type: "error", message: response.message || "حذف اعلان ناموفق بود." });
        return;
      }
      setNotifications((prev) => prev.filter((item) => item.id !== id));
      setMessage({ type: "success", message: "اعلان با موفقیت حذف شد." });
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
    }
  };

  return (
    <section className="space-y-4">
      <AdminPageTitle title="اعلان‌ها" />

      <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
        <h3 className="mb-4 text-sm font-semibold text-slate-200">ارسال اعلان عمومی</h3>
        <p className="mb-4 text-xs text-slate-400">این اعلان برای همه کاربران ارسال می‌شود.</p>
        <NotificationSendForm userId={0} onSuccess={() => void loadNotifications()} />
      </div>

      {loading ? (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 text-slate-300">
          در حال دریافت اعلان‌ها...
        </div>
      ) : !notifications.length ? (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 text-slate-300">اعلانی یافت نشد.</div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/60">
            <table className="min-w-full divide-y divide-slate-800 text-sm">
              <thead className="bg-slate-900/80">
                <tr>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">شناسه</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">گیرنده</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">عنوان</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">متن</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">نوع</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">تاریخ</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">عملیات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {notifications.map((notification) => (
                  <tr key={notification.id} className="transition hover:bg-slate-900">
                    <td className="whitespace-nowrap px-4 py-3 text-slate-300">{notification.id}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-200">{recipientLabel(notification)}</td>
                    <td className="max-w-[10rem] truncate px-4 py-3 text-slate-100" title={notification.title}>
                      {notification.title || "—"}
                    </td>
                    <td className="max-w-xs truncate px-4 py-3 text-slate-300" title={notification.content}>
                      {notification.content || "—"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-300">{notification.type == 'duel_game' ? 'بازی دو نفره' : "اطلاع رسانی"}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-400 ltr text-right">
                      {notification.created_at ? notification.created_at : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => void onDelete(notification.id)}
                        className="inline-flex rounded-lg border border-rose-800 px-3 py-1.5 text-xs text-rose-200 transition hover:border-rose-600"
                      >
                        حذف
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pagination ? <PaginationView pagination={pagination} onPageChange={setPage} /> : null}
        </>
      )}
    </section>
  );
}
